import {
  BitmapText,
  Container,
  Graphics,
  Sprite,
  Texture,
  type DestroyOptions,
  type RenderLayer as PixiRenderLayer,
} from 'pixi.js';
import { TILE_HALF } from '../sim/projection';
import type { Direction } from '../sim/direction';
import type { DirectionFrames, FrameTexture, PlayerFrames } from './playerArt';
import { layerZIndex, type RenderLayer } from './layerOrder';
import { OVERHEAD_FONT_FAMILY, OVERHEAD_FONT_SIZE } from './bakedFont';
import { CombatTextLayer, combatFloatColor, type CombatFloatPunch } from './combatText';
import { UoHueSprite } from './uoHueBatch';

/** Frames per second of the walk cycle while the mobile is stepping. */
const WALK_FPS = 12;
/**
 * Frames per second of the run cycle. Faster cadence than the walk so the run
 * gait reads as a distinct, quicker gait on screen (the run step is also ~2x
 * faster in the sim, so the legs keep pace with the ground).
 */
const RUN_FPS = 20;
/** Frames per second of the one-shot fidget the mobile plays while at rest. */
const FIDGET_FPS = 8;
/**
 * Frames per second of a one-shot action clip (the attack swing, the rat death;
 * ADR-0116). The classic client plays character actions around this cadence -
 * quick enough to read as a single decisive motion, slow enough that a ~5-7
 * frame clip is legible.
 */
const ACTION_FPS = 10;
/** First frame of a clip; also the single frame of the static stand pose. */
const START_FRAME = 0;
/**
 * "Recently walking" grace window: after the sim stops reporting movement we keep
 * the gait cycling for this long before settling to the idle stand pose. This
 * mirrors UO's `PLAYER_WALKING_DELAY` (ClassicUO `IsWalking`/`NoIterateAnimIndex`):
 * a direction change costs a brief in-place turn beat during which the mobile is
 * momentarily "not moving", and the client keeps the walk/run animation alive
 * across that gap rather than snapping to the standing pose. Without this hold the
 * character visibly freezes on the stand frame every time you turn while moving.
 */
const WALK_HOLD_MS = 150;
/**
 * After the mobile has stood still this long it plays the fidget once, then
 * waits this long again. A random spread keeps the fidgets from looking metronomic.
 */
const FIDGET_DELAY_MIN_MS = 4000;
const FIDGET_DELAY_MAX_MS = 9000;
/**
 * Drops the sprite from the tile's top corner (where the container sits) to the
 * tile's visual centre, so the character's feet rest on the diamond rather than
 * floating at its back edge.
 */
const GROUND_OFFSET_Y = TILE_HALF;
const VISUAL_SCALE_TRANSITION_MS = 600;

/**
 * Fallback overhead anchor when body art is unavailable.
 * Live body frames replace it with their measured top edge plus the ClassicUO
 * eight-pixel clearance.
 */
const NAME_OFFSET_Y = -52;

/**
 * How long a spoken line floats above the head before it has fully faded out
 * (Slice 5). A few seconds is the UO feel: long enough to read in a crowd, short
 * enough that the overhead surface clears itself. The journal keeps the history;
 * this surface is deliberately transient.
 */
const SPEECH_TTL_MS = 5000;
/** The trailing slice of {@link SPEECH_TTL_MS} over which a line fades to nothing. */
const SPEECH_FADE_MS = 1200;
/**
 * Ceiling on how many overhead lines stack above one character at once. Rapid
 * chatter (or any future accumulation path) drops the oldest line rather than
 * letting the stack grow without bound and tower off-screen. A small number keeps
 * the newest, most relevant lines readable; the journal keeps the full history.
 */
const MAX_OVERHEAD_LINES = 4;
/** Vertical gap between stacked speech lines, and from the name label to the first. */
const SPEECH_LINE_GAP = 15;
/** Classic-style baseline gap from the overhead name to its equipped title. */
const TITLE_LINE_GAP = 18;
/**
 * Breathing room left between two stacked speech messages, on top of the measured
 * height of the lower one: every line - single or wrapped - steps past its own full
 * height plus this constant, so lines never overlap whatever the font's line height.
 *
 * This is a small fixed positive value, NOT `SPEECH_LINE_GAP - OVERHEAD_FONT_SIZE`
 * as it once was: that derivation only stayed positive while the font was shorter
 * than the gap (true of the old flat-colour stub font at 12px, false of the real
 * baked fonts.mul at 22px), where it silently went negative and overlapped lines by
 * `OVERHEAD_FONT_SIZE - SPEECH_LINE_GAP` px. 3px keeps unwrapped stacking under the
 * stub font pixel-identical to that era (12 + 3 == the old 15px step).
 */
const SPEECH_LINE_PAD = 3;
/**
 * Maximum width, in px, an overhead speech line grows to before it word-wraps to a
 * new line (issue: long lines ran off-screen on one line). A fixed comfortable
 * width rather than an exact ClassicUO match: ~5 tiles wide ({@link TILE_HALF} is
 * 22, so a tile is 44px), enough for a handful of words per line so a sentence
 * reads in two or three centred lines without dominating the viewport or trailing
 * off the edge. The original 160px (~3.6 tiles) wrapped too eagerly - ordinary
 * lines broke into skinny columns - so it was widened to 220px (ADR-0082): wide
 * enough that everyday lines read on one or two lines, still narrow enough that a
 * genuinely long sentence wraps rather than running off-screen. Wrapping breaks on
 * word boundaries; {@link breakWords} hard-breaks a lone token longer than this so
 * it can never overflow. Tune here.
 */
const SPEECH_WRAP_WIDTH = 220;

/**
 * How long a name floats above a head once revealed before it has fully faded out
 * (issue #85). The name is no longer an always-on label: it is revealed on first
 * appearance in the viewport, on login, and on a left-click, then fades. One shared
 * interval drives all of those triggers so the world reads consistently - a few
 * seconds is long enough to read a passer-by, short enough that the overhead clears
 * itself and the scene isn't cluttered with permanent floating names. Tune here.
 */
const NAME_REVEAL_MS = 4000;
/** The trailing slice of {@link NAME_REVEAL_MS} over which the name fades to nothing. */
const NAME_FADE_MS = 1000;

/**
 * The colour of an overhead name. In the original 2D game an innocent player or
 * NPC name renders blue; criminals/murderers shift it grey/red. Nearly everyone
 * here is innocent, so names draw in the classic UO innocent blue by default.
 * The baked blackletter glyph already carries its own dark outline, so this fill
 * tints that glyph.
 */
const NAME_COLOR_INNOCENT = 0x0080ff;
/** Equipped achievement titles use the app's warm gold, distinct from blue names. */
export const TITLE_COLOR = 0xd8c690;

/**
 * The classic UO **criminal grey** name colour (the notoriety the King's Justice
 * thief carries, ADR-0194 - he is a criminal, alive for his one instant and as a
 * corpse, so his name never reads innocent blue). The renderer applies it via
 * {@link PlayerSprite.setNameColor} off the replicated `justice` marker.
 */
export const NAME_COLOR_CRIMINAL = 0x9c9c9c;

/**
 * zIndex for the overhead text (name + speech). The body/equipment layers sort by
 * their facing-dependent draw order (small indices, 0..n); the labels sit far
 * above so they always draw over the whole character stack regardless of how the
 * layers reorder on a facing change.
 */
const LABEL_Z = 1000;

/**
 * The overhead duel health bar (Player duels, PRD #252): a slim bar surfacing a
 * duelist's replicated {@link HealthComponent} while they fight, shown only in the
 * combat stance and hidden otherwise. Because health is replicated, every client
 * draws the same fraction for a given fighter. Styled to the app's warm design
 * system rather than a placeholder: a dark panel track with the standard border,
 * a gold fill that drains left-to-right. All tunable.
 */
const HEALTH_BAR_WIDTH = 42;
const HEALTH_BAR_HEIGHT = 6;
/** How far above the ground point the bar floats: just above the head, below the name. */
const HEALTH_BAR_OFFSET_Y = -46;
/** Corner radius of the track (matches the app's small-control rounding). */
const HEALTH_BAR_RADIUS = 3;
/** The app panel surface / border (index.html `.whatsnew-card`, `#toolbar button`). */
const HEALTH_BAR_TRACK_FILL = 0x2b2820;
const HEALTH_BAR_BORDER = 0x4a4334;
/** The app's gold accent (`#d8c690`), the healthy fill. */
const HEALTH_BAR_FILL = 0xd8c690;
/** Arena runner variant: beneath the feet, with a danger-red fill. */
const ARENA_HEALTH_BAR_OFFSET_Y = 22;
const ARENA_HEALTH_BAR_FILL = 0xa62822;
/** Sphere Duel poison variant: a toxic green fill signalling the poison status. */
const HEALTH_BAR_POISON_FILL = 0x5fbf3f;
/** Sphere Duel local mana sits directly below the underfoot health bar. */
const MANA_BAR_OFFSET_Y = ARENA_HEALTH_BAR_OFFSET_Y + HEALTH_BAR_HEIGHT;
const MANA_BAR_FILL = 0x3576b8;
/** zIndex just under the text labels, so a name/damage number still reads over it. */
const HEALTH_BAR_Z = LABEL_Z - 1;

/**
 * Maximum gap between two taps on a character for them to count as a double-click
 * (open-your-own-wardrobe shortcut). A generous window in the classic UO
 * double-click range: long enough for a relaxed double-click, short enough that two
 * deliberately separate taps don't merge. The first tap still reveals the name
 * (issue #85) as any single tap does; the second within this window additionally
 * fires {@link PlayerSprite.onDoubleClick}.
 */
const DOUBLE_CLICK_MS = 300;

/** One active overhead line: its label and how long it has left before clearing. */
interface SpeechLine {
  label: BitmapText;
  remainingMs: number;
  // The label's measured pixel height, cached at creation (item 5 / ADR-0135).
  // Reading `BitmapText.height` re-measures the glyph run (a bounds recompute) on
  // every access; the text of a spoken line never changes once created, so its
  // height is measured once here and reused by {@link PlayerSprite.layoutSpeech}
  // instead of re-measuring every line on every relayout.
  height: number;
}

/**
 * How many spent damage-number labels {@link combatLabelPool} keeps warm.
 *
 * Sized well above the concurrent float population of a dense Arena fight (six
 * per character, and only the characters actually being hit carry any) so the
 * steady state never constructs, while still bounding what a one-off cascade
 * leaves resident.
 */
const COMBAT_LABEL_POOL_MAX = 64;

/**
 * Spent damage-number labels, shared by every character sprite (PRD #826).
 *
 * A damage number lives 900ms, and a sustained Arena fight produces them
 * continuously across the whole crowd, so building a `BitmapText` per hit and
 * destroying it on expiry made combat floats the client's steadiest source of
 * display-object churn. Recycling them costs a text and fill assignment instead.
 *
 * The pool is deliberately process-wide rather than per sprite: floats are short
 * and bursty, so the labels one dying enemy releases are exactly the ones the
 * next one needs, and a shared list retains the concurrent peak rather than a
 * per-character high-water mark for every character on the field.
 */
const combatLabelPool: BitmapText[] = [];

/** How many labels the shared damage-number pool is currently holding. */
export function combatLabelPoolSize(): number {
  return combatLabelPool.length;
}

/**
 * Empty the shared damage-number pool, destroying what it holds. The pool
 * outlives any one renderer, so a test that counts label construction resets it
 * first; production never needs this.
 */
export function resetCombatLabelPool(): void {
  for (const label of combatLabelPool) label.destroy();
  combatLabelPool.length = 0;
}

/**
 * Take a damage-number label carrying `text` in `fill`, recycled from
 * {@link combatLabelPool} when one is free and built when it is not.
 *
 * A recycled label is reset to a newborn float's presentation - full alpha, unit
 * scale, origin offset - because {@link PlayerSprite.layoutCombatText} only
 * writes position and scale for floats the pure layer still lists, so a label
 * that arrived mid-frame would otherwise briefly wear the faded, scattered
 * resting state of the float before it.
 */
function acquireCombatLabel(text: string, fill: number): BitmapText {
  const pooled = combatLabelPool.pop();
  if (pooled) {
    pooled.text = text;
    pooled.style.fill = fill;
    pooled.alpha = 1;
    pooled.scale.set(1);
    pooled.position.set(0, 0);
    return pooled;
  }
  const label = new BitmapText({
    text,
    style: {
      fontFamily: OVERHEAD_FONT_FAMILY,
      fontSize: OVERHEAD_FONT_SIZE,
      fill,
      align: 'center',
    },
  });
  label.anchor.set(0.5, 1);
  // Above the speech labels so a rising number is never occluded by a chat line.
  label.zIndex = LABEL_Z + 1;
  return label;
}

function randomFidgetDelay(): number {
  return FIDGET_DELAY_MIN_MS + Math.random() * (FIDGET_DELAY_MAX_MS - FIDGET_DELAY_MIN_MS);
}

/**
 * Look one clip up in a layer's {@link PlayerFrames} **by name**. The one-shot
 * action broadcast carries an open clip name (`attack`, `die`, whatever a later
 * event bakes), so the lookup is dynamic; an unknown name resolves `undefined`
 * and the callers treat it as "this layer has no such clip" (a safe no-op /
 * hold-the-stand-pose), never an error.
 */
function clipByName(clips: PlayerFrames, name: string): DirectionFrames | undefined {
  return (clips as unknown as Partial<Record<string, DirectionFrames>>)[name];
}

/**
 * One layer of a character's render stack: a baked clip set, the render slot it
 * occupies (for the facing-dependent draw order), and optional per-layer hue data.
 * The body is the base layer; equipment (hair and beard, and later clothing)
 * are additional layers stacked over it. Every layer is driven by the one shared
 * animation clock, so they stay in lockstep.
 */
export interface SpriteLayer {
  clips: PlayerFrames;
  layer: RenderLayer;
  hue?: number;
  partialHue?: boolean;
  /** Optional multiplier for this layer alone (default 1). */
  visualScale?: number;
  /** Anchor used when applying `visualScale` (default `center`). */
  scaleOrigin?: 'center' | 'ground';
  /**
   * Optional upward shift in pixels for this layer alone (default 0), used to seat a
   * rider on a mount whose back is a different height from the UO horse the seated art
   * was authored against (ADR-0442). Positive raises the layer.
   *
   * Either one value for every facing, or eight in {@link Direction} order. A mount's
   * back meets the viewer at a different screen height in each facing - a head-on
   * animal is foreshortened, a side-on one is not - so a single number cannot seat a
   * rider correctly all the way round.
   */
  offsetY?: number | readonly number[];
  /** Additional lift used while this layer is showing its resting pose. */
  idleOffsetY?: number | readonly number[];
}

/**
 * Expand a layer's lift into the per-facing form {@link ActiveLayer} holds: a single
 * number applies to every facing, an eight-entry list is used as given, and anything
 * else (absent, wrong length, non-finite) means no lift at all.
 */
function normalizeOffsetY(offsetY: number | readonly number[] | undefined): readonly number[] {
  if (typeof offsetY === 'number') {
    return Number.isFinite(offsetY) ? new Array(8).fill(offsetY) : ZERO_OFFSETS;
  }
  if (Array.isArray(offsetY) && offsetY.length === 8 && offsetY.every((v) => Number.isFinite(v))) {
    return offsetY as readonly number[];
  }
  return ZERO_OFFSETS;
}

const ZERO_OFFSETS: readonly number[] = new Array(8).fill(0);

/** Internal per-layer state: its clips/slot plus the Pixi child drawing it. */
interface ActiveLayer {
  clips: PlayerFrames;
  layer: RenderLayer;
  sprite: UoHueSprite;
  hue: number;
  partialHue: boolean;
  visualScale: number;
  scaleOrigin: 'center' | 'ground';
  /** Per-facing lift, always eight entries in {@link Direction} order. */
  offsetY: readonly number[];
  /** Additional per-facing lift used only while resting. */
  idleOffsetY: readonly number[];
}

/**
 * A character mobile, drawn from real UO art baked from `anim.mul` as an **ordered
 * stack of independently-hued animation layers** (body + equipment like hair).
 * Each layer is a flat billboard {@link Sprite} whose texture swaps with the
 * current facing and frame; all layers share the *one* animation clock below, so
 * the gait, the run/walk cadence, and the fidget timing stay in lockstep across
 * the whole stack. Driven by the sim's `moving` flag each tick:
 *  - moving: the walk cycle loops at {@link WALK_FPS} (or run at {@link RUN_FPS});
 *  - at rest: it holds the static stand pose, and every few seconds plays the
 *    fidget through once (a shift/stretch) before returning to the stand.
 * Each layer positions its own frame by that frame's own UO anchor
 * (`imageCenterX/Y`), so body and equipment self-register and stay aligned across
 * every action and facing. The draw order within the stack is facing-dependent
 * (see {@link ./layerOrder}) and re-sorts when the character turns.
 *
 * The grayscale UO art is recoloured by the shared UO hue sprite batch shader.
 * Per-layer vertex data selects the `hues.mul` gradient so body and equipment can
 * be hued independently without breaking the Pixi batch.
 * Without hue data the raw grayscale base shows through.
 */
export class PlayerSprite extends Container {
  // The render stack, body first. The body (`layers[0]`) owns the canonical
  // frame counts the shared clock advances against; every other layer clamps the
  // shared index into its own range, so a short clip can't throw or desync. Built in
  // the constructor and rebuilt in place by {@link setLayers} when the worn outfit
  // changes (the reference is stable; only its contents are swapped).
  private readonly layers: ActiveLayer[] = [];
  private facing: Direction;
  // Whether the gait cycle is showing right now. Stays true through the brief
  // "recently walking" grace window (WALK_HOLD_MS) so a turn beat doesn't drop to
  // the idle stand pose. Distinct from the sim's instantaneous `isMoving`.
  private gaiting = false;
  private running = false;
  // Whether the character is carrying a one-handed item and so walks/runs with the
  // ARMED gait (UO anim groups Walk_Armed 1 / Run_Armed 3), whose frames carry the
  // hand-bounce. Set from the layer stack in {@link setLayers}: the `accessory` slot
  // is a held light (torch/candle/lantern, ADR-0063), the one carried item we render.
  // Matches ClassicUO switching to the armed group when something is in hand
  // (`GetGroupForAnimation`); per-layer fallback to the unarmed gait where an armed
  // clip isn't baked (ADR-0277, superseding the ADR-0219 runtime bob stopgap).
  private armed = false;
  // Time since the sim last reported movement (0 while moving); drives the grace
  // window that bridges a turn beat without resetting the gait.
  private gaitHoldMs = 0;

  // Gait-cycle state (used while gaiting, shared by the walk and run clips so
  // toggling the run flag mid-stride keeps the pose rather than snapping to
  // frame 0).
  private walkIndex = START_FRAME;
  private walkTimer = 0;

  // Idle state (used while at rest): count up to the next fidget, then play the
  // fidget clip through once. `fidgetIndex < 0` means "standing, not fidgeting".
  private idleTimer = 0;
  private nextFidgetAt = randomFidgetDelay();
  private fidgetIndex = -1;
  private fidgetTimer = 0;

  // One-shot action-clip state (ADR-0116): while `actionClip` names a clip (e.g.
  // 'attack', 'die'), the sprite plays it through once on the action clock and
  // then returns to standing. It preempts the gait/idle machines and pins the
  // broadcast facing for its duration. `undefined` means "no action playing".
  private actionClip?: string;
  private actionIndex = START_FRAME;
  private actionTimer = 0;
  // Loops left on the active clip (the dance emote, ADR-0387). 1 is the historical
  // one-shot; a repeating clip restarts from its first frame each time it runs out
  // and only returns to standing once this reaches 0. Never interacts with the
  // death-hold below: a die clip is always a single pinned pass.
  private actionLoopsLeft = 1;
  // Death-hold (Duel slice D, ADR-0131): a `die-*` one-shot pins on its final
  // (prone) frame instead of returning to the stand pose, so a downed duelist
  // stays dropped through the ~2.5s death beat. `actionHold` is armed by
  // {@link playAction} for a death clip; `actionHeld` latches once the clip has
  // reached and pinned that last frame. Cleared by {@link clearDeath} on
  // resurrection (or by any fresh non-death action).
  private actionHold = false;
  private actionHeld = false;

  // Replicated duel combat stance (Duel slice D, ADR-0131). When set, the idle
  // clip becomes the combat idle (action 7) and the walk clip the combat advance
  // (action 15), so both duelists and every bystander see the pose. Driven each
  // frame by the renderer from the entity's replicated `renderable.combatStance`.
  private combatStance = false;
  // The combat-idle loop cursor (used only while `combatStance` and standing): the
  // ready stance cycles continuously rather than holding a single frame + fidget.
  private combatIdleIndex = START_FRAME;
  private combatIdleTimer = 0;

  // Replicated visual-size transition state. The first observed scale is adopted
  // immediately so a joining viewer sees the authoritative size. Later target
  // changes interpolate, making an admin grow or shrink visibly instead of snapping.
  private visualScaleInitialized = false;
  private visualScaleStart = 1;
  private visualScaleCurrent = 1;
  private visualScaleTarget = 1;
  private visualScaleElapsedMs = 0;

  // Floating combat text (Duel slice D, ADR-0131): rising red damage numbers
  // over this sprite (a miss floats nothing). The pure {@link CombatTextLayer} owns the lifecycle; the map
  // pairs each live float id with its drawn BitmapText so it can be torn down the
  // moment the float expires. Advanced from update() on the shared render clock.
  private readonly combatText = new CombatTextLayer();
  private readonly combatLabels = new Map<number, BitmapText>();

  // The overhead duel health bar (Player duels, PRD #252). Built lazily the first
  // time a duel shows it (so only fighters ever carry the extra display objects);
  // `fill` scales on its x from the left edge to the current health fraction, so a
  // hit just changes `fill.scale.x` - no redraw. `healthBarShow` remembers the
  // in-duel gate so line-of-sight toggling (setOverheadVisible) composes with it.
  private healthBar?: Container;
  private healthBarArena = false;
  private healthBarPoisoned = false;
  private healthFill?: Graphics;
  private healthBarShow = false;
  private healthFraction = 1;
  private manaBar?: Container;
  private manaFill?: Graphics;
  private manaBarShow = false;
  private manaFraction = 1;

  // Fading overhead speech (Slice 5): newest line nearest the head, older lines
  // stacked above. Each fades out over its last SPEECH_FADE_MS and is removed
  // when its time elapses. Advanced from update() so it shares the render clock.
  private readonly speechLines: SpeechLine[] = [];
  /** Bottom anchor shared by names, speech, and combat text for the live body frame. */
  private overheadAnchorY = NAME_OFFSET_Y;

  // The name label floating above the head, if this character has a name. Held so
  // per-viewer line-of-sight can toggle it (./regions, ADR-0017) the way speech
  // lines are toggled - a viewer across the bank wall draws neither.
  private nameLabel?: BitmapText;
  private titleLabel?: BitmapText;
  /**
   * The banishment mm:ss countdown floated above the head while this character
   * serves a Wandering Moongate island sentence (PRD #362 Slice D). Lazily built
   * on the first {@link setBanishCountdown}; unlike the name it never fades -
   * a legible sentence is the point - and it clears when the component clears.
   */
  private banishLabel?: BitmapText;
  // How long the name has left before it has fully faded out (issue #85). The name
  // is transient, not always-on: it is revealed on viewport entry, login, and click
  // (see revealName) and counts down here on the render clock, just like a speech
  // line. Zero means hidden. Advanced from update() so it shares the render clock.
  private nameRemainingMs = 0;
  // Whether this character's overhead (name + speech) shows for the local viewer
  // this frame. Set per-frame by the renderer from the inside/outside region test;
  // remembered so a speech line arriving between frames inherits the right state.
  private overheadVisible = true;

  // Timestamp (render-clock ms) of the last primary tap on this character, for
  // double-click detection. Starts at -Infinity so the very first tap can never
  // read as the second half of a double-click. Reset to -Infinity once a
  // double-click fires, so a third rapid tap starts a fresh pair rather than
  // firing again. See {@link onDoubleClick} and the constructor's tap handler.
  private lastTapMs = Number.NEGATIVE_INFINITY;

  /**
   * Called when this character is double-clicked (two primary taps within
   * {@link DOUBLE_CLICK_MS}). The renderer wires it to open the paperdoll for
   * player characters (self + duel-target/dueling roles, PRD #629) and to the
   * marker-keyed NPC affordances (see {@link ./gameRenderer}). Absent when no
   * handler is attached (the headless tests), in which case a double-click is a
   * harmless no-op beyond the usual name reveal.
   */
  onDoubleClick?: () => void;

  /**
   * The shared war-mode notoriety-highlight hue currently applied to this
   * sprite's layers, or `null` when not highlighted (Rat Swarm Slice E).
   */
  private highlightHue: number | null = null;

  /**
   * The hit-flash whiten mix copied to every layer vertex.
   *
   * It applies after hueing, so the sprite whitens from its hued look exactly as
   * the former filter chain did.
   */
  private flashMix = 0;
  private readonly interactionEnabled: boolean;

  // Optional always-on-top render layer for the overhead labels (./gameRenderer).
  // When supplied, the name and each speech line are attached to it so they draw
  // over every wall/roof regardless of how this sprite depth-sorts; the labels
  // stay logical children here, so their positioning, fade, and visibility logic is
  // unchanged. Absent in the splash preview and headless tests, where labels draw
  // as ordinary children.
  private readonly overheadLayer?: PixiRenderLayer;

  constructor(
    layers: SpriteLayer[],
    initialFacing: Direction,
    name?: string,
    overheadLayer?: PixiRenderLayer,
    title?: string,
    options?: { interactive?: boolean },
  ) {
    super();
    this.facing = initialFacing;
    this.overheadLayer = overheadLayer;
    this.interactionEnabled = options?.interactive ?? true;
    // The stack sorts its children by the facing-dependent layer order; the
    // overhead labels sit above the lot via a fixed high zIndex.
    this.sortableChildren = true;

    // Build the body + equipment layer stack (see setLayers): one Pixi child per
    // layer with batch-compatible hue data, then order and draw the rest frame.
    this.setLayers(layers);

    // The chosen display name floats just above the head, centred on the
    // character, so the crowd is legible. A high zIndex keeps it over every body
    // and equipment layer regardless of how they reorder. Absent name draws none.
    // It starts hidden: the name is transient (issue #85) and only shows once the
    // renderer reveals it (viewport entry, login) or the viewer clicks the character.
    if (name) {
      // Drawn in the baked UO blackletter font (the classic 2D names/speech
      // font). The glyph's black outline and light fill are baked into the atlas,
      // so there is no Pixi stroke: this fill tints the name over its baked-in
      // outline. Innocent names render blue, as in the original game. See ./bakedFont.
      const label = new BitmapText({
        text: name,
        style: {
          fontFamily: OVERHEAD_FONT_FAMILY,
          fontSize: OVERHEAD_FONT_SIZE,
          fill: NAME_COLOR_INNOCENT,
          align: 'center',
        },
      });
      label.anchor.set(0.5, 1);
      label.position.set(0, this.overheadAnchorY);
      label.zIndex = LABEL_Z;
      label.visible = false;
      this.addChild(label);
      // Draw the name in the always-on-top overhead pass so it never clips behind a
      // wall (it stays this sprite's child for positioning + fade). See ./gameRenderer.
      this.overheadLayer?.attach(label);
      this.nameLabel = label;
    }
    this.setTitle(title);

    // Clicking the character re-reveals its name (issue #85). The body and
    // equipment layers form the clickable silhouette; a `pointertap` on any of
    // them bubbles to this container's handler. In Pixi v8 `pointertap` fires for
    // ANY button (button-specific events are `click`/`rightclick`), so a
    // right-click on a character also re-reveals - benign and consistent with the
    // existing door `pointertap` pattern; right-click movement (main.ts) still
    // runs. The player is now a hit-test target sharing the depth-sorted world
    // container with door sprites, so a character whose on-screen bounds overlap a
    // door (e.g. parked on the threshold) can intercept the tap and reveal its
    // name on those pixels instead of routing UseDoor; the door stays clickable on
    // its non-overlapped pixels. This partial, situational overlap is an accepted
    // POC limitation, not a guarantee door taps are wholly unaffected.
    // `cursor: inherit` keeps the viewport's gauntlet over the character.
    this.eventMode = this.interactionEnabled ? 'static' : 'none';
    this.cursor = 'inherit';
    // Each layer sprite is made an interaction target in setLayers, so a rebuilt
    // stack stays clickable; the container's handler below catches the bubbled tap.
    // Every tap reveals the name (issue #85); a second tap within DOUBLE_CLICK_MS
    // additionally fires onDoubleClick (own-character wardrobe shortcut). The single
    // tap's name reveal is benign, so the double-click is layered on top of it
    // rather than suppressing it.
    // `pointertap` fires for ANY button in Pixi v8, so the double-click detector
    // additionally gates on the primary button (PRD #629 story 26) - a right- or
    // middle-button pair must never read as a double-click, while the single-tap
    // name reveal stays any-button as before. A bare test `emit` carries no event
    // payload and counts as primary (the headless-drive convention).
    if (this.interactionEnabled) {
      this.on('pointertap', (e) => {
        this.revealName();
        const button = (e as { button?: number } | undefined)?.button ?? 0;
        if (button === 0) this.registerTap();
      });
    }
  }

  /**
   * Fold one primary tap into the double-click detector: if it lands within
   * {@link DOUBLE_CLICK_MS} of the previous tap on this character, fire
   * {@link onDoubleClick} and consume the pair; otherwise record it as the first
   * tap of a potential pair. Uses the render clock (`performance.now`) rather than
   * the event payload so the headless tests can drive it with a bare `emit`.
   */
  private registerTap(): void {
    const now = performance.now();
    if (now - this.lastTapMs <= DOUBLE_CLICK_MS) {
      this.lastTapMs = Number.NEGATIVE_INFINITY;
      this.onDoubleClick?.();
    } else {
      this.lastTapMs = now;
    }
  }

  /**
   * Replace the body + equipment render stack in place (Cosmetic Progression live
   * dressing fix, PRD #101): destroy the current layer sprites and build fresh ones
   * from `specs`, keeping the container's facing, gait/idle animation state, and the
   * overhead name/speech labels intact. The renderer calls this when an entity's worn
   * outfit changes on the snapshot stream, so an equip/unequip shows live on every
   * client without a reconnect. Each new layer sprite gets its own hue data and is
   * made an interaction target (so the silhouette stays clickable), then the stack is
   * re-sorted to the facing-dependent order and redrawn at the current shared frame so
   * the swap is seamless. Only the body/equipment children are touched - the name and
   * speech labels are separate children and are left untouched.
   */
  setLayers(specs: SpriteLayer[]): void {
    for (const l of this.layers) {
      this.removeChild(l.sprite);
      l.sprite.destroy();
    }
    this.layers.length = 0;
    for (const spec of specs) {
      const sprite = new UoHueSprite(spec.hue, spec.partialHue);
      sprite.eventMode = this.interactionEnabled ? 'static' : 'none';
      this.addChild(sprite);
      this.layers.push({
        clips: spec.clips,
        layer: spec.layer,
        sprite,
        hue: spec.hue ?? 0,
        partialHue: spec.partialHue ?? false,
        visualScale:
          Number.isFinite(spec.visualScale) && (spec.visualScale ?? 0) > 0
            ? spec.visualScale!
            : 1,
        scaleOrigin: spec.scaleOrigin ?? 'center',
        offsetY: normalizeOffsetY(spec.offsetY),
        idleOffsetY: normalizeOffsetY(spec.idleOffsetY),
      });
    }
    // A held light (the `accessory` slot) puts a one-hander in hand, so the whole
    // stack walks/runs with the armed gait (see {@link gaitFrames}).
    this.armed = specs.some((spec) => spec.layer === 'accessory');
    this.applyLayerRenderState();
    this.applyLayerOrder();
    this.applyFrame();
  }

  /**
   * Re-dress this sprite for a different character and clear every trace of the
   * previous one, so a pooled sprite is indistinguishable from a fresh build
   * (PRD #826).
   *
   * The renderer drives position, facing, gait, flash, and scale from replicated
   * state every frame, so those self-heal. What does not is the state a sprite
   * only ever leaves: the held death pose that made it a corpse, the corpse
   * fade's residual alpha, the notoriety highlight, and the transient overhead
   * surfaces. The visual-scale transition is reset to uninitialised as well, so
   * the next entity adopts its own size on its first frame instead of growing
   * into it from the previous occupant's.
   */
  protected resetForReuse(layers: SpriteLayer[], initialFacing: Direction): void {
    // Facing first: `setLayers` draws the rest frame as it rebuilds the stack, and
    // it should draw the new character's, not the retired one's.
    this.facing = initialFacing;
    this.setLayers(layers);

    // The die pose and its freeze; without this a recycled sprite spawns dead.
    this.actionClip = undefined;
    this.actionIndex = START_FRAME;
    this.actionTimer = 0;
    this.actionLoopsLeft = 1;
    this.actionHold = false;
    this.actionHeld = false;

    this.gaiting = false;
    this.running = false;
    this.gaitHoldMs = 0;
    this.combatStance = false;
    this.combatIdleIndex = START_FRAME;
    this.combatIdleTimer = 0;
    this.startWalking();
    this.startResting();

    // The corpse fade leaves a partly transparent container behind.
    this.alpha = 1;
    this.visible = true;
    this.eventMode = this.interactionEnabled ? 'static' : 'none';
    this.cursor = 'inherit';

    this.visualScaleInitialized = false;
    this.visualScaleStart = 1;
    this.visualScaleCurrent = 1;
    this.visualScaleTarget = 1;
    this.visualScaleElapsedMs = 0;
    this.scale.set(1);

    this.highlightHue = null;
    this.flashMix = 0;
    this.overheadVisible = true;
    this.lastTapMs = Number.NEGATIVE_INFINITY;

    this.clearSpeech();
    for (const label of this.combatLabels.values()) this.releaseCombatLabel(label);
    this.combatLabels.clear();
    this.combatText.clear();

    this.applyLayerRenderState();
    this.applyFrame();
  }

  /**
   * Whether the war-mode notoriety highlight is currently applied (Rat Swarm
   * Slice E). Exposed for the renderer's war-off sweep and the headless tests.
   */
  get highlighted(): boolean {
    return this.highlightHue !== null;
  }

  /** Number of body and equipment quads submitted for this character. */
  get visualLayerCount(): number {
    return this.layers.length;
  }

  /** Number of submitted quads carrying an encoded hue mode. */
  get huedLayerCount(): number {
    return this.layers.reduce(
      (count, layer) => count + (layer.sprite.hue === 0 ? 0 : 1),
      0,
    );
  }

  /** Whether a non-idle, time-sensitive presentation is currently advancing. */
  get requiresActivePresentation(): boolean {
    return (
      this.actionClip !== undefined ||
      this.speechLines.length > 0 ||
      this.combatText.size > 0 ||
      this.nameRemainingMs > 0 ||
      this.visualScaleElapsedMs < VISUAL_SCALE_TRANSITION_MS
    );
  }

  /**
   * Apply or clear the notoriety highlight hue.
   *
   * While set, it replaces each layer's own hue and forces full-hue mode across
   * the silhouette.
   */
  setHighlight(hue: number | null): void {
    if (this.highlightHue === hue) return;
    this.highlightHue = hue;
    this.applyLayerRenderState();
  }

  /**
   * Apply a 0..1 hit-flash whiten mix after the hue lookup.
   */
  setFlashMix(mix: number): void {
    const clamped = Math.min(1, Math.max(0, mix));
    if (this.flashMix === clamped) return;
    this.flashMix = clamped;
    this.applyLayerRenderState();
  }

  /** Copy the current hue, highlight, and flash state onto every batch sprite. */
  private applyLayerRenderState(): void {
    for (const l of this.layers) {
      l.sprite.hue = this.highlightHue ?? l.hue;
      l.sprite.partialHue = this.highlightHue === null && l.partialHue;
      l.sprite.flashMix = this.flashMix;
      l.sprite.filters = null;
    }
  }

  /**
   * Tear the whole character down and detach overhead labels from the always-on-top render
   * layer while they are still live, before `super.destroy` destroys them as
   * children. The detach mirrors the constructor's/`say`'s attach: this class owns
   * the attach, so it owns the detach, rather than leaning on the implicit detach
   * Pixi's `removeChildren` happens to do for a destroyed child's render layer.
   */
  override destroy(options?: DestroyOptions): void {
    if (this.destroyed) return;
    this.layers.length = 0;
    if (this.nameLabel) this.overheadLayer?.detach(this.nameLabel);
    if (this.titleLabel) this.overheadLayer?.detach(this.titleLabel);
    if (this.banishLabel) this.overheadLayer?.detach(this.banishLabel);
    for (const line of this.speechLines) this.overheadLayer?.detach(line.label);
    // Damage labels are pooled, not owned: release them (which also removes them
    // as children) before `super.destroy` would otherwise destroy the pool's stock
    // along with this sprite.
    for (const label of this.combatLabels.values()) this.releaseCombatLabel(label);
    this.combatLabels.clear();
    if (this.healthBar) this.overheadLayer?.detach(this.healthBar);
    if (this.manaBar) this.overheadLayer?.detach(this.manaBar);
    super.destroy(options);
  }

  /**
   * Show (or update, or clear with `null`) the banishment countdown above this
   * character's head (PRD #362 Slice D). `text` is the already-formatted mm:ss -
   * the renderer owns the extrapolation between the server's ~1/s pushes (the
   * storm-clock pattern); this sprite just draws the string. Sits a line above
   * the name slot so the two never overlap, in the action-emote yellow, in the
   * always-on-top overhead pass like every other overhead surface.
   */
  setBanishCountdown(text: string | null): void {
    if (text === null) {
      if (this.banishLabel) {
        this.overheadLayer?.detach(this.banishLabel);
        this.removeChild(this.banishLabel);
        this.banishLabel.destroy();
        this.banishLabel = undefined;
      }
      return;
    }
    if (!this.banishLabel) {
      const label = new BitmapText({
        text,
        style: {
          fontFamily: OVERHEAD_FONT_FAMILY,
          fontSize: OVERHEAD_FONT_SIZE,
          fill: 0xffdd55, // the action-emote yellow family: an event notice, not speech
          align: 'center',
        },
      });
      label.anchor.set(0.5, 1);
      label.position.set(0, this.overheadAnchorY - 16); // one line above the name slot
      label.zIndex = LABEL_Z;
      this.addChild(label);
      this.overheadLayer?.attach(label);
      this.banishLabel = label;
    }
    if (this.banishLabel.text !== text) this.banishLabel.text = text;
  }

  /**
   * Reveal this character's name above the head and (re)start the shared fade
   * timer (issue #85). Called on first appearance in the viewport, on login, and
   * on a left-click. A fresh reveal while the name is still showing simply
   * restarts the countdown rather than stacking. The name still obeys per-viewer
   * line-of-sight (ADR-0017): it shows only while the viewer shares the
   * character's region, so a reveal across the bank wall stays dark until the
   * viewer can see in. No-op for an unnamed mobile.
   */
  revealName(): void {
    if (!this.nameLabel) return;
    // If the name had faded out, the speech stack has dropped into its slot; the
    // name reclaiming the slot must lift the stack back above it.
    const wasOccupying = this.nameOccupiesSlot();
    this.nameRemainingMs = NAME_REVEAL_MS;
    this.nameLabel.alpha = 1;
    this.nameLabel.visible = this.overheadVisible;
    if (this.titleLabel) {
      this.titleLabel.alpha = 1;
      this.titleLabel.visible = this.overheadVisible;
    }
    if (!wasOccupying) this.layoutSpeech();
  }

  /**
   * Update this character's overhead name text in place (ADR-0194). Names are
   * normally immutable for a sprite's lifetime (a player never renames
   * mid-session), so `reconcile` only calls this for the King's Justice thief,
   * whose replicated name flips to `a corpse of <name>` when the halberd fells
   * him. Idempotent (a same-text call is a no-op) and a no-op on an unnamed
   * sprite; the label keeps its colour, slot, and fade state.
   */
  setName(name: string | undefined): void {
    if (!this.nameLabel || name === undefined) return;
    if (this.nameLabel.text === name) return;
    this.nameLabel.text = name;
  }

  /** Reconcile the server-resolved equipped title without rebuilding the sprite. */
  setTitle(title: string | undefined): void {
    if (!title) {
      if (this.titleLabel) {
        this.overheadLayer?.detach(this.titleLabel);
        this.removeChild(this.titleLabel);
        this.titleLabel.destroy();
        this.titleLabel = undefined;
        this.layoutSpeech();
      }
      return;
    }
    if (!this.titleLabel) {
      const label = new BitmapText({
        text: title,
        style: {
          fontFamily: OVERHEAD_FONT_FAMILY,
          fontSize: OVERHEAD_FONT_SIZE,
          fill: TITLE_COLOR,
          align: 'center',
        },
      });
      label.anchor.set(0.5, 1);
      label.position.set(0, this.overheadAnchorY - TITLE_LINE_GAP);
      label.zIndex = LABEL_Z;
      label.visible = this.overheadVisible && this.nameRemainingMs > 0;
      this.addChild(label);
      this.overheadLayer?.attach(label);
      this.titleLabel = label;
      this.layoutSpeech();
    } else if (this.titleLabel.text !== title) {
      this.titleLabel.text = title;
    }
  }

  /**
   * Tint this character's overhead name (ADR-0194): the notoriety colour. Every
   * name defaults to the classic innocent blue; the renderer flips the King's
   * Justice thief - a criminal, alive and as a corpse - to
   * {@link NAME_COLOR_CRIMINAL} grey off the replicated `justice` marker. No-op
   * on an unnamed sprite.
   */
  setNameColor(color: number): void {
    if (!this.nameLabel) return;
    this.nameLabel.style.fill = color;
  }

  /**
   * Whether the name currently occupies the bottom slot of the overhead stack: it
   * exists and its reveal timer has not run out. While it does, speech stacks above
   * it; once it has faded (or the mobile is nameless) speech drops into this slot so
   * a faded name never leaves a gap over the head. Line-of-sight visibility does not
   * count here - a name hidden only because the viewer is across a wall still owns
   * its slot, so the layout doesn't shuffle as the viewer moves in and out of sight.
   */
  private nameOccupiesSlot(): boolean {
    return this.nameLabel !== undefined && this.nameRemainingMs > 0;
  }

  /**
   * Show or hide this character's overhead surface (name label + every active
   * speech line) for the local viewer. Per-viewer line-of-sight (ADR-0017) calls
   * this each frame: an entity across the bank wall from the viewer draws no name
   * and no overhead chat, so inside and outside read as separate spaces. The name
   * additionally only shows while its reveal timer is still running (issue #85), so
   * a faded-out name stays dark even with clear line-of-sight. A pure render toggle
   * - the body still draws and the world is untouched. The flag is remembered so a
   * line spoken before the next frame inherits the same state.
   */
  setOverheadVisible(visible: boolean): void {
    this.overheadVisible = visible;
    // The name shows only when the viewer can see it AND its reveal timer is still
    // running (issue #85): line-of-sight gates it like speech, but a faded-out name
    // stays dark even with clear line-of-sight until it is revealed again.
    if (this.nameLabel) this.nameLabel.visible = visible && this.nameRemainingMs > 0;
    if (this.titleLabel) this.titleLabel.visible = visible && this.nameRemainingMs > 0;
    for (const line of this.speechLines) line.label.visible = visible;
    for (const label of this.combatLabels.values()) label.visible = visible;
    // The health bar composes line-of-sight with its own in-duel gate: a bystander
    // across the bank wall sees neither the fighters' names nor their health bars.
    if (this.healthBar) this.healthBar.visible = visible && this.healthBarShow;
    if (this.manaBar) this.manaBar.visible = visible && this.manaBarShow;
  }

  setFacing(dir: Direction): void {
    if (dir === this.facing) return;
    // A one-shot action clip plays in its broadcast facing for its whole (sub-
    // second) duration: the per-frame sync keeps pushing the snapshot facing,
    // which lags the broadcast by RTT and would yank the swing sideways
    // mid-clip. Normal turning resumes the moment the clip completes.
    if (this.actionClip !== undefined) return;
    this.facing = dir;
    // The draw order flips with facing, so re-sort the stack before redrawing.
    // Keep the current frame index so turning mid-cycle doesn't reset the pose.
    this.applyLayerOrder();
    this.applyFrame();
  }

  /**
   * Rotate a pinned one-shot without restarting its animation.
   * Whirlwind uses this to cycle all eight directional attack sprites while
   * the swing's frame timer keeps advancing.
   */
  setActionFacing(dir: Direction): void {
    if (this.actionClip === undefined) {
      this.setFacing(dir);
      return;
    }
    if (dir === this.facing) return;
    this.facing = dir;
    this.applyLayerOrder();
    this.applyFrame();
  }

  /**
   * Apply a per-entity visual scale multiplier (PRD: larger enemies) while
   * keeping the character's **feet planted** on its tile. Pixi scales a
   * container about its local origin (0,0), but the character's ground point
   * (its feet) sits at container-local `y = GROUND_OFFSET_Y`, not at the origin
   * (see {@link applyFrame}: each frame draws at `-cy - height + GROUND_OFFSET_Y`,
   * so the registered ground pixel lands `GROUND_OFFSET_Y` below the origin).
   * A naive `scale.set(2)` would therefore push the feet down by
   * `(scale-1) * GROUND_OFFSET_Y` and sink the sprite into the ground. We
   * compensate by lifting the container the same amount, so the feet stay at
   * `position.y + GROUND_OFFSET_Y` (the tile's visual centre) at any scale while
   * the body grows upward from there. `groundY` is the y the renderer just set
   * (the tile's top corner, before the ground drop); we keep that contract
   * external and fold the compensation in here so the constant stays local to
   * this sprite. `scale <= 0`/NaN is coerced to 1 (a bad value never hides a
   * mobile). Overhead labels and bars receive the inverse scale, so they move
   * with the taller or shorter sprite but retain their normal visual size.
   */
  setVisualScale(scale: number, groundY: number): void {
    const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
    this.visualScaleInitialized = true;
    this.visualScaleStart = s;
    this.visualScaleCurrent = s;
    this.visualScaleTarget = s;
    this.visualScaleElapsedMs = VISUAL_SCALE_TRANSITION_MS;
    this.scale.set(s);
    this.applyOverheadVisualScale();
    // Lift by the extra ground drop the scale introduced, so the feet stay put.
    this.position.y = groundY - (s - 1) * GROUND_OFFSET_Y;
  }

  /**
   * Adopt the first replicated scale immediately, then animate later changes over a
   * short fixed interval. The ground compensation is recalculated every frame, so
   * the character grows upward from its planted feet throughout the transition.
   */
  updateVisualScale(scale: number, groundY: number, dtMs: number): void {
    const target = Number.isFinite(scale) && scale > 0 ? scale : 1;
    if (!this.visualScaleInitialized) {
      this.setVisualScale(target, groundY);
      return;
    }
    if (target !== this.visualScaleTarget) {
      this.visualScaleStart = this.visualScaleCurrent;
      this.visualScaleTarget = target;
      this.visualScaleElapsedMs = 0;
    }
    this.visualScaleElapsedMs = Math.min(
      VISUAL_SCALE_TRANSITION_MS,
      this.visualScaleElapsedMs + Math.max(0, dtMs),
    );
    const t = this.visualScaleElapsedMs / VISUAL_SCALE_TRANSITION_MS;
    const eased = t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2;
    this.visualScaleCurrent =
      this.visualScaleStart + (this.visualScaleTarget - this.visualScaleStart) * eased;
    this.scale.set(this.visualScaleCurrent);
    this.applyOverheadVisualScale();
    this.position.y = groundY - (this.visualScaleCurrent - 1) * GROUND_OFFSET_Y;
  }

  /**
   * Cancel the character container's visual-size multiplier for UI that floats
   * overhead. Its position still rides the outer transform, keeping it above the
   * scaled body, while its rendered text and bars remain the standard pixel size.
   */
  private applyOverheadVisualScale(): void {
    const inverse = 1 / this.visualScaleCurrent;
    if (this.nameLabel) this.nameLabel.scale.set(inverse);
    if (this.titleLabel) this.titleLabel.scale.set(inverse);
    if (this.banishLabel) this.banishLabel.scale.set(inverse);
    for (const line of this.speechLines) line.label.scale.set(inverse);
    if (this.healthBar) this.healthBar.scale.set(inverse);
    if (this.manaBar) this.manaBar.scale.set(inverse);
    this.layoutCombatText();
  }

  /** Snapshot only the visible body/equipment stack for a fading speed trail. */
  createAfterimage(alpha: number): Container {
    const afterimage = new Container();
    afterimage.eventMode = 'none';
    afterimage.position.copyFrom(this.position);
    afterimage.scale.copyFrom(this.scale);
    afterimage.alpha = alpha;
    afterimage.zIndex = this.zIndex - 0.25;
    for (const layer of this.layers) {
      const ghost = new Sprite(layer.sprite.texture);
      ghost.position.copyFrom(layer.sprite.position);
      ghost.scale.copyFrom(layer.sprite.scale);
      ghost.anchor.copyFrom(layer.sprite.anchor);
      ghost.pivot.copyFrom(layer.sprite.pivot);
      ghost.rotation = layer.sprite.rotation;
      ghost.tint = layer.sprite.tint;
      ghost.filters = layer.sprite.filters
        ? [...layer.sprite.filters]
        : null;
      ghost.zIndex = layer.sprite.zIndex;
      afterimage.addChild(ghost);
    }
    afterimage.sortableChildren = true;
    return afterimage;
  }

  /** Re-sort the stack: each layer takes its facing-dependent z within the container. */
  private applyLayerOrder(): void {
    for (const l of this.layers) l.sprite.zIndex = layerZIndex(l.layer, this.facing);
  }

  /**
   * Advance the animation. While moving, loops the walk or run cycle (`running`
   * selects which, at a run-appropriate cadence); while at rest, stands still
   * and fires the one-shot fidget on a randomised timer. Toggling `running`
   * mid-stride keeps the current frame index so the gait doesn't snap.
   */
  update(dtMs: number, moving: boolean, running = false): void {
    // A one-shot action clip (ADR-0116) preempts the gait/idle machines: it plays
    // through on its own clock and hands back to a fresh idle state when done (a
    // still-moving mobile resumes its gait on the next update). The overhead
    // surfaces still age normally - a swing must not freeze a name or a line.
    if (this.actionClip !== undefined) {
      // A REPEATING clip (the dance emote, ADR-0387) runs for seconds, not the
      // sub-second one-shot the pinned facing above assumes, so real movement
      // cancels the remaining loops: the current pass finishes and hands straight
      // back to the gait rather than sliding across tiles frozen mid-gesture. A
      // one-shot (repeat 1) is untouched - a swing/bow/die still plays through.
      if (moving && this.actionLoopsLeft > 1) this.actionLoopsLeft = 1;
      this.advanceAction(dtMs);
      this.advanceName(dtMs);
      this.advanceSpeech(dtMs);
      this.advanceCombatText(dtMs);
      return;
    }

    // Adopt the run flag only from real movement: during a turn beat the sim
    // reports not-moving/not-running, and we want to hold the prior gait (run vs
    // walk) across the gap rather than flicker run -> walk -> run.
    if (moving) {
      this.running = running;
      this.gaitHoldMs = 0;
    } else {
      this.gaitHoldMs += dtMs;
    }

    // Keep cycling the gait through a brief not-moving gap (a turn beat or a
    // frame-aligned step boundary); only settle to idle once the grace window
    // lapses. From a dead stop the gap is never "recent", so a stationary pivot
    // still shows the rotating stand pose.
    const showGait = moving || (this.gaiting && this.gaitHoldMs <= WALK_HOLD_MS);
    if (showGait !== this.gaiting) {
      this.gaiting = showGait;
      if (showGait) this.startWalking();
      else this.startResting();
      this.applyFrame();
    }

    if (this.gaiting) this.advanceWalk(dtMs);
    else this.advanceIdle(dtMs);

    this.advanceName(dtMs);
    this.advanceSpeech(dtMs);
    this.advanceCombatText(dtMs);
  }

  /** Age the name reveal, fade its tail, and hide it once the timer elapses. */
  private advanceName(dtMs: number): void {
    if (!this.nameLabel || this.nameRemainingMs <= 0) return;
    this.nameRemainingMs -= dtMs;
    if (this.nameRemainingMs <= 0) {
      this.nameRemainingMs = 0;
      this.nameLabel.visible = false;
      if (this.titleLabel) this.titleLabel.visible = false;
      // The name just vacated its slot: drop the speech stack down so no gap is
      // left over the head where the name used to be.
      this.layoutSpeech();
      return;
    }
    this.nameLabel.alpha =
      this.nameRemainingMs < NAME_FADE_MS ? this.nameRemainingMs / NAME_FADE_MS : 1;
    if (this.titleLabel) this.titleLabel.alpha = this.nameLabel.alpha;
  }

  /**
   * Float a spoken line above this character's head in its hue (Slice 5). The
   * line is plain-text (Pixi `BitmapText` never interprets markup), drawn over the
   * body and the name, and stacked above any still-visible earlier lines. It fades
   * and clears itself on the render clock via {@link advanceSpeech}; the journal
   * keeps the permanent record. `color` is the speaker's hue as a packed RGB.
   *
   * Rendered in the baked UO font: the fill colour tints the glyph's near-white
   * fill to the speaker's hue while the baked-in black outline stays black (the
   * font keeps Pixi's `applyFillAsTint`), so no Pixi stroke is needed. See
   * ./bakedFont.
   *
   * A long line **word-wraps** to multiple centred lines at {@link SPEECH_WRAP_WIDTH}
   * rather than running off-screen on one line. Pixi's bitmap-font layout honours
   * `wordWrap`/`wordWrapWidth`/`align` natively against the baked glyph metrics, and
   * `breakWords` falls back to a hard break for a lone token wider than the line. The
   * label is bottom-anchored (below), so the wrapped block grows **upward** away from
   * the head; {@link layoutSpeech} stacks any older lines clear of its full height.
   */
  say(text: string, color: number): void {
    const label = new BitmapText({
      text,
      style: {
        fontFamily: OVERHEAD_FONT_FAMILY,
        fontSize: OVERHEAD_FONT_SIZE,
        fill: color,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: SPEECH_WRAP_WIDTH,
        breakWords: true,
      },
    });
    label.anchor.set(0.5, 1);
    label.zIndex = LABEL_Z;
    // Inherit the viewer's current line-of-sight verdict: if this character is
    // across the bank wall from the viewer, the new line stays hidden until the
    // viewer shares its region again (ADR-0017).
    label.visible = this.overheadVisible;
    this.addChild(label);
    // Same always-on-top pass as the name: speech draws over walls/roofs while
    // staying a child here for its stacking + fade. dropOverheadLabel undoes both
    // the attach and the child when the line clears. See ./gameRenderer.
    this.overheadLayer?.attach(label);
    // Measure the label's height once, now, and cache it: the line's text is fixed
    // for its lifetime, so every later relayout reuses this rather than re-measuring
    // the glyph run each time (item 5 / ADR-0135).
    this.speechLines.push({ label, remainingMs: SPEECH_TTL_MS, height: label.height });
    // Cap the stack height: past MAX_OVERHEAD_LINES, evict the oldest (the bottom of
    // the array is the oldest line) so a burst of chatter can never tower an unbounded
    // column of text off-screen above one head.
    while (this.speechLines.length > MAX_OVERHEAD_LINES) {
      this.dropOverheadLabel(this.speechLines.shift()!.label);
    }
    this.layoutSpeech();
  }

  /**
   * Drop every active overhead line at once, right now, off the render clock. Used
   * on tab refocus ({@link GameRenderer.clearAllSpeech}): while the tab is hidden the
   * rAF-backed ticker is paused so {@link advanceSpeech} never ages lines out, yet the
   * socket keeps delivering barks - so on return the whole hidden-period backlog would
   * otherwise appear at once and fade together. Clearing it means lines the viewer
   * never actually saw do not flood the surface. The journal keeps player speech, so
   * nothing durable is lost. Shares advanceSpeech's teardown (dropOverheadLabel).
   */
  clearSpeech(): void {
    if (this.speechLines.length === 0) return;
    for (const line of this.speechLines) this.dropOverheadLabel(line.label);
    this.speechLines.length = 0;
  }

  /**
   * Tear one overhead label all the way down: detach it from the always-on-top
   * render layer, remove it as a child, and destroy it. The explicit detach is the
   * counterpart of the attach in the constructor/{@link say} - this class did the
   * attach, so it does the detach - rather than trusting `removeChild`'s implicit
   * render-layer detach, whose absence in an earlier Pixi was a per-line leak: the
   * layer's child array kept every destroyed label alive for the tab's life (#200).
   */
  private dropOverheadLabel(label: BitmapText): void {
    this.overheadLayer?.detach(label);
    this.removeChild(label);
    label.destroy();
  }

  /**
   * Restack the active lines bottom-up: the newest line takes the bottom of the
   * stack, older ones climb above it. The bottom sits just above the name while the
   * name occupies its slot; once the name has faded (or the mobile is nameless) the
   * stack drops into the name's slot ({@link NAME_OFFSET_Y}) so a faded name never
   * leaves a gap over the head.
   */
  private layoutSpeech(): void {
    let y = this.nameOccupiesSlot()
      ? this.overheadAnchorY -
        (this.titleLabel ? TITLE_LINE_GAP + SPEECH_LINE_GAP : SPEECH_LINE_GAP)
      : this.overheadAnchorY;
    for (let i = this.speechLines.length - 1; i >= 0; i--) {
      const line = this.speechLines[i]!;
      line.label.position.set(0, y);
      // Bottom-anchored labels grow upward, so step past this label's full measured
      // height before placing the next (older) one above it, plus a fixed
      // SPEECH_LINE_PAD of breathing room. Stepping the *measured* height means a
      // wrapped multi-line line is cleared in full just as a single line is, so the
      // predecessor never overlaps it regardless of the font's line height. The
      // height is the cached measurement from creation (item 5 / ADR-0135), not a
      // fresh `label.height` re-measure each relayout.
      y -= line.height + SPEECH_LINE_PAD;
    }
  }

  /** Age every active line, fade its tail, and drop the ones whose time is up. */
  private advanceSpeech(dtMs: number): void {
    if (this.speechLines.length === 0) return;
    let removed = false;
    for (let i = this.speechLines.length - 1; i >= 0; i--) {
      const line = this.speechLines[i]!;
      line.remainingMs -= dtMs;
      if (line.remainingMs <= 0) {
        this.dropOverheadLabel(line.label);
        this.speechLines.splice(i, 1);
        removed = true;
        continue;
      }
      line.label.alpha =
        line.remainingMs < SPEECH_FADE_MS ? line.remainingMs / SPEECH_FADE_MS : 1;
    }
    if (removed) this.layoutSpeech();
  }

  /**
   * Float one combat number over this sprite (Duel slice D, ADR-0131): a landed
   * hit's red damage number over a struck duelist (a miss floats nothing). Builds a
   * BitmapText, registers it with the pure {@link CombatTextLayer} lifecycle, and
   * lets {@link advanceCombatText} rise-and-fade it. Over the layer's cap the
   * oldest float's label is torn down in the same stroke.
   *
   * The optional `punch` is the arena damage-number punch (PRD #430 Slice B,
   * ADR-0223): magnitude-scaled pop + seeded scatter + white-hot grade, all
   * derived inside the pure layer. Town/duel callers pass nothing and render
   * pixel-identical to before.
   */
  spawnCombatText(text: string, color: number, punch?: CombatFloatPunch): void {
    const { id, evicted } = this.combatText.spawn(text, color, punch);
    for (const goneId of evicted) this.dropCombatLabel(goneId);
    // The white-hot top-band grade (identical to `color` without a punch),
    // matching the float's own graded color in the pure layer.
    const label = acquireCombatLabel(text, combatFloatColor(color, punch?.magnitude));
    label.visible = this.overheadVisible;
    this.addChild(label);
    this.overheadLayer?.attach(label);
    this.combatLabels.set(id, label);
    this.layoutCombatText();
  }

  /** Age every combat float, reposition/fade the survivors, drop the expired. */
  private advanceCombatText(dtMs: number): void {
    if (this.combatText.size === 0) return;
    for (const id of this.combatText.advance(dtMs)) this.dropCombatLabel(id);
    this.layoutCombatText();
  }

  /** Place each live float at its derived rise + scatter + pop + fade. */
  private layoutCombatText(): void {
    for (const float of this.combatText.active()) {
      const label = this.combatLabels.get(float.id);
      if (!label) continue;
      // offsetX/scale are the punch fields (ADR-0223): exactly 0 / 1 on the
      // default town/duel path, so that path stays pixel-identical.
      label.position.set(float.offsetX, this.overheadAnchorY + float.offsetY);
      label.scale.set(float.scale / this.visualScaleCurrent);
      label.alpha = this.overheadVisible ? float.alpha : 0;
    }
  }

  /** Retire one combat float's label: detach, remove, and hand it back to the pool. */
  private dropCombatLabel(id: number): void {
    const label = this.combatLabels.get(id);
    if (!label) return;
    this.releaseCombatLabel(label);
    this.combatLabels.delete(id);
  }

  /**
   * Detach a spent damage label from this sprite and return it to the shared
   * pool. Over {@link COMBAT_LABEL_POOL_MAX} the surplus is destroyed instead, so
   * a one-off cascade cannot leave an unbounded pool resident.
   */
  private releaseCombatLabel(label: BitmapText): void {
    this.overheadLayer?.detach(label);
    this.removeChild(label);
    if (combatLabelPool.length >= COMBAT_LABEL_POOL_MAX) {
      label.destroy();
      return;
    }
    label.visible = false;
    combatLabelPool.push(label);
  }

  /**
   * Surface (or hide) this character's duel health bar (Player duels, PRD #252).
   * The renderer pushes this each frame from the entity's replicated health and its
   * combat stance: `show` is true only while the fighter is in the combat stance, so
   * the bar appears with the war cursor when combat opens and clears at duel end.
   * `current`/`max` come straight off the replicated {@link HealthComponent}, so the
   * bar reads identically on every client. The bar is built on first show and then
   * just re-scaled, so the hot path (a landed hit) is a single `scale.x` assignment.
   */
  setHealthBar(current: number, max: number, show: boolean, arena = false, poisoned = false): void {
    this.healthBarShow = show;
    if (!show) {
      if (this.healthBar) this.healthBar.visible = false;
      return;
    }
    if (!this.healthBar || this.healthBarArena !== arena) {
      this.dropHealthBar();
      this.healthBarArena = arena;
      this.buildHealthBar();
    }
    // Poison recolours the fill green for its whole duration and reverts on cure/expiry.
    // Colour rides `fill.tint` (a cheap per-frame assignment), so no redraw is needed.
    this.healthBarPoisoned = poisoned;
    if (this.healthFill) this.healthFill.tint = this.healthBarFillColor();
    // Clamp to [0, 1]; a zero/absent max reads as empty rather than dividing by zero.
    this.healthFraction = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
    if (this.healthFill) this.healthFill.scale.x = this.healthFraction;
    if (this.healthBar) this.healthBar.visible = this.overheadVisible;
  }

  /** The fill colour for the current bar mode: green while poisoned else gold/red. */
  private healthBarFillColor(): number {
    if (this.healthBarPoisoned) return HEALTH_BAR_POISON_FILL;
    return this.healthBarArena ? ARENA_HEALTH_BAR_FILL : HEALTH_BAR_FILL;
  }

  /** Show the local Sphere Duel player's private mana directly below health. */
  setManaBar(current: number, max: number, show: boolean): void {
    this.manaBarShow = show;
    if (!show) {
      if (this.manaBar) this.manaBar.visible = false;
      return;
    }
    if (!this.manaBar) this.buildManaBar();
    this.manaFraction = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
    if (this.manaFill) this.manaFill.scale.x = this.manaFraction;
    if (this.manaBar) this.manaBar.visible = this.overheadVisible;
  }

  private buildManaBar(): void {
    const bar = new Container();
    bar.position.set(0, MANA_BAR_OFFSET_Y);
    bar.zIndex = HEALTH_BAR_Z;
    const track = new Graphics();
    track
      .roundRect(-HEALTH_BAR_WIDTH / 2, -HEALTH_BAR_HEIGHT / 2, HEALTH_BAR_WIDTH, HEALTH_BAR_HEIGHT, HEALTH_BAR_RADIUS)
      .fill(HEALTH_BAR_TRACK_FILL)
      .stroke({ color: HEALTH_BAR_BORDER, width: 1, alignment: 0.5 });
    const inset = 1;
    const fill = new Graphics();
    fill
      .roundRect(
        0,
        -(HEALTH_BAR_HEIGHT - inset * 2) / 2,
        HEALTH_BAR_WIDTH - inset * 2,
        HEALTH_BAR_HEIGHT - inset * 2,
        HEALTH_BAR_RADIUS - 1,
      )
      .fill(MANA_BAR_FILL);
    fill.position.set(-HEALTH_BAR_WIDTH / 2 + inset, 0);
    fill.scale.x = this.manaFraction;
    bar.addChild(track, fill);
    bar.visible = this.overheadVisible;
    this.addChild(bar);
    this.overheadLayer?.attach(bar);
    this.manaBar = bar;
    this.manaFill = fill;
  }

  /**
   * Build the overhead health bar's display objects once (a warm-panel track plus a
   * gold fill), attach them to the always-on-top overhead layer like the name so the
   * bar never clips behind a wall, and leave the fill scaled to the last known
   * fraction. Called lazily from {@link setHealthBar} the first time a duel shows it.
   */
  private buildHealthBar(): void {
    const bar = new Container();
    bar.position.set(0, this.healthBarArena ? ARENA_HEALTH_BAR_OFFSET_Y : HEALTH_BAR_OFFSET_Y);
    bar.zIndex = HEALTH_BAR_Z;

    const track = new Graphics();
    track
      .roundRect(-HEALTH_BAR_WIDTH / 2, -HEALTH_BAR_HEIGHT / 2, HEALTH_BAR_WIDTH, HEALTH_BAR_HEIGHT, HEALTH_BAR_RADIUS)
      .fill(HEALTH_BAR_TRACK_FILL)
      .stroke({ color: HEALTH_BAR_BORDER, width: 1, alignment: 0.5 });

    // The fill is drawn from the track's inner-left edge at full width, then scaled on
    // its x to the health fraction; anchoring the Graphics origin at the left edge
    // makes it drain rightward. Inset 1px so the gold sits inside the border.
    const inset = 1;
    const fillWidth = HEALTH_BAR_WIDTH - inset * 2;
    const fill = new Graphics();
    // Draw white and colour via `tint`, so poison (green) toggles with a single
    // assignment and never needs the bar rebuilt.
    fill
      .roundRect(0, -(HEALTH_BAR_HEIGHT - inset * 2) / 2, fillWidth, HEALTH_BAR_HEIGHT - inset * 2, HEALTH_BAR_RADIUS - 1)
      .fill(0xffffff);
    fill.tint = this.healthBarFillColor();
    fill.position.set(-HEALTH_BAR_WIDTH / 2 + inset, 0);
    fill.scale.x = this.healthFraction;

    bar.addChild(track, fill);
    bar.visible = this.overheadVisible;
    this.addChild(bar);
    this.overheadLayer?.attach(bar);
    this.healthBar = bar;
    this.healthFill = fill;
  }

  /** Remove the current mode's bar before rebuilding it in the other style. */
  private dropHealthBar(): void {
    if (!this.healthBar) return;
    this.overheadLayer?.detach(this.healthBar);
    this.removeChild(this.healthBar);
    this.healthBar.destroy({ children: true });
    this.healthBar = undefined;
    this.healthFill = undefined;
  }

  /**
   * Play one body clip through **once** in the given facing, then return to
   * standing (the one-shot action-animation broadcast, Rat Swarm Slice B /
   * ADR-0116 - the stomper's attack swing, the rat's death clip). The clip is
   * looked up **by name** in the body layer's {@link PlayerFrames}, so future
   * clips need no new render plumbing; an unknown name, or a clip with no baked
   * frames for this facing (unbaked art, a stub that pre-dates the clip), is a
   * **safe no-op** - the sprite simply keeps doing what it was doing. The
   * overhead name/speech labels are separate children and are untouched. While
   * the clip plays, the broadcast facing is pinned (see {@link setFacing}) and
   * the gait/idle machines are preempted (see {@link update}); a fresh
   * `playAction` mid-clip restarts with the new clip (last broadcast wins).
   *
   * `repeat` plays the clip through that many times before returning to standing
   * (the dance emote's {@link ../sim/content.DANCE_REPEAT} loop, ADR-0387); the
   * default 1 is the historical one-shot, and a value below 1 clamps to it. The loop
   * is purely local playback - it holds nothing and pins no sim state - but it does
   * run for seconds, so unlike a one-shot it is **cancellable by movement**: an
   * {@link update} with `moving` drops the remaining loops (the current pass finishes,
   * then the gait resumes). A later broadcast preempts it like any clip; a stationary
   * dancer keeps the pinned broadcast facing for the whole gesture.
   */
  playAction(clip: string, facing: Direction, repeat = 1): void {
    const frames = clipByName(this.bodyClips(), clip)?.[facing];
    if (!frames || frames.length === 0) return; // unknown clip / unbaked facing
    // Adopt the broadcast facing through the normal turn path (re-sorts layers),
    // before pinning it for the clip's duration. Clear any in-flight one-shot
    // first: setFacing ignores turns while a clip is pinned, so a fresh
    // broadcast mid-clip would otherwise restart in the stale facing.
    this.actionClip = undefined;
    this.setFacing(facing);
    this.actionClip = clip;
    this.actionIndex = START_FRAME;
    this.actionTimer = 0;
    this.actionLoopsLeft = Number.isFinite(repeat) ? Math.max(1, Math.floor(repeat)) : 1;
    // A death clip pins on its final (prone) frame so the corpse stays dropped
    // for its whole linger (ADR-0131), rather than popping back upright a few
    // frames in. This covers the player's directional duel deaths
    // (`die-forward`/`die-back`, lifted by clearDeath on resurrection) AND a
    // monster's single `die` clip (arena enemies, storm vortexes, swarm rats),
    // which are always terminal - the entity is removed after its corpse linger
    // (RAT_CORPSE_TICKS server-side; an arena enemy despawns after just its die
    // clip, ARENA_ENEMY_DESPAWN_TICKS, and its frozen sprite lives on as the
    // renderer's client-side TTL corpse, ADR-0304) - so the held frame needs no
    // clear. Without this a longer linger (the arena's ten-second corpses) would
    // show a *standing* monster, not a body on the ground.
    this.actionHold = clip === 'die-forward' || clip === 'die-back' || clip === 'die';
    this.actionHeld = false;
    this.applyFrame();
  }

  /**
   * Whether this sprite is playing-or-holding a terminal die clip (true from the
   * moment {@link playAction} starts one). The renderer's despawn pass reads it to
   * tell a slain arena horde enemy - whose frozen sprite stays behind as a
   * client-side TTL corpse (ADR-0304) - from a live one despawning for any other
   * reason (recycled, cleared, stream switch), which is destroyed as before.
   */
  get deathHeld(): boolean {
    return this.actionHold;
  }

  /**
   * Clear a held death pose (Duel slice D, ADR-0131): the renderer calls this when
   * a duelist is resurrected (its replicated health jumps back to full), lifting
   * the pinned die frame and handing back to the ordinary idle/gait machines. A
   * no-op on a sprite that is not holding a death frame, so it is safe to call on
   * any duel-end signal.
   */
  clearDeath(): void {
    if (!this.actionHold && !this.actionHeld && this.actionClip === undefined) return;
    this.actionHold = false;
    this.actionHeld = false;
    this.actionClip = undefined;
    this.actionLoopsLeft = 1;
    this.gaiting = false;
    this.startResting();
    this.applyFrame();
  }

  /**
   * Set the replicated duel combat stance (Duel slice D, ADR-0131). While on, the
   * idle clip is the combat idle (action 7) and the walk clip the combat advance
   * (action 15), degrading to the stand/walk pose when those clips are unbaked.
   * Restarts the combat-idle loop on entry so the ready stance reads cleanly. A
   * no-op when unchanged.
   */
  setCombatStance(on: boolean): void {
    if (on === this.combatStance) return;
    this.combatStance = on;
    this.combatIdleIndex = START_FRAME;
    this.combatIdleTimer = 0;
    this.applyFrame();
  }

  /**
   * Advance the active one-shot clip on the action clock; when the last frame's
   * time elapses, clear it and hand back to a fresh idle state (the next
   * {@link update} resumes the gait if the mobile is moving). The body layer's
   * frame count drives the one-shot; shorter layers clamp in
   * {@link currentFrame}, exactly as the fidget does.
   */
  private advanceAction(dtMs: number): void {
    if (this.actionClip === undefined) return;
    // A held death pose is frozen on its last frame - stop advancing entirely
    // until clearDeath() lifts it (ADR-0131), so the corpse stays dropped.
    if (this.actionHeld) return;
    const msPerFrame = 1000 / ACTION_FPS;
    const count = clipByName(this.bodyClips(), this.actionClip)?.[this.facing]?.length ?? 0;
    this.actionTimer += dtMs;
    let changed = false;
    while (this.actionTimer >= msPerFrame) {
      this.actionTimer -= msPerFrame;
      this.actionIndex++;
      changed = true;
      if (this.actionIndex >= count) {
        if (this.actionHold) {
          // A duel death clip: pin the final (prone) frame and latch, keeping
          // actionClip set so currentFrame keeps drawing the downed pose.
          this.actionIndex = Math.max(START_FRAME, count - 1);
          this.actionHeld = true;
          break;
        }
        // A repeating clip (the dance emote, ADR-0387) restarts from its first frame
        // and keeps the pinned facing, so the gesture reads as one continuous dance
        // rather than five separate broadcasts.
        if (this.actionLoopsLeft > 1) {
          this.actionLoopsLeft--;
          this.actionIndex = START_FRAME;
          continue;
        }
        // One-shot done: back to standing (never a loop), normal turning resumes.
        this.actionClip = undefined;
        this.gaiting = false;
        this.startResting();
        break;
      }
    }
    if (changed) this.applyFrame();
  }

  /** The body layer's clips: the canonical timeline the shared clock counts off. */
  private bodyClips(): PlayerFrames {
    return this.layers[0]!.clips;
  }

  /**
   * The gait frames one layer plays right now for the current facing, honouring
   * the duel combat stance (ADR-0131): the combat advance (action 15) while in
   * stance, else the ordinary run/walk cycle. Falls back to the run/walk cycle
   * when this layer has no baked combat-advance, so an unbaked or partial stack
   * degrades cleanly (the per-layer clamp philosophy).
   */
  private gaitFrames(clips: PlayerFrames): FrameTexture[] | undefined {
    if (this.combatStance) {
      const advance = clipByName(clips, 'combat-advance')?.[this.facing];
      if (advance && advance.length > 0) return advance;
    }
    // Carrying a one-handed light: play the ARMED gait (UO groups Walk_Armed 1 /
    // Run_Armed 3), whose frames carry the hand-bounce the unarmed groups 0/2 lack
    // for held lights (ADR-0277). Per-layer fallback to the unarmed gait keeps an
    // unbaked or partial layer in lockstep rather than blanking it.
    if (this.armed) {
      const armed = clipByName(clips, this.running ? 'run-armed' : 'walk-armed')?.[this.facing];
      if (armed && armed.length > 0) return armed;
    }
    return (this.running ? clips.run : clips.walk)[this.facing];
  }

  /**
   * The combat-idle frames one layer plays while in stance and standing (action 7),
   * or `undefined` when not in stance / unbaked so the caller holds the ordinary
   * stand pose (ADR-0131).
   */
  private combatIdleFrames(clips: PlayerFrames): FrameTexture[] | undefined {
    if (!this.combatStance) return undefined;
    const idle = clipByName(clips, 'combat-idle')?.[this.facing];
    return idle && idle.length > 0 ? idle : undefined;
  }

  private startWalking(): void {
    this.walkIndex = START_FRAME;
    this.walkTimer = 0;
  }

  private startResting(): void {
    this.idleTimer = 0;
    this.nextFidgetAt = randomFidgetDelay();
    this.fidgetIndex = -1;
    this.fidgetTimer = 0;
  }

  private advanceWalk(dtMs: number): void {
    const msPerFrame = 1000 / (this.running ? RUN_FPS : WALK_FPS);
    this.walkTimer += dtMs;
    let stepped = false;
    while (this.walkTimer >= msPerFrame) {
      this.walkTimer -= msPerFrame;
      const count = this.gaitFrames(this.bodyClips())?.length ?? 1;
      this.walkIndex = (this.walkIndex + 1) % count;
      stepped = true;
    }
    if (stepped) this.applyFrame();
  }

  private advanceIdle(dtMs: number): void {
    // In the duel combat stance, the ready pose (combat idle, action 7) loops
    // continuously instead of the stand-pose-plus-fidget cycle (ADR-0131).
    if (this.combatStance && this.combatIdleFrames(this.bodyClips())) {
      const msPerFrame = 1000 / ACTION_FPS;
      this.combatIdleTimer += dtMs;
      let changed = false;
      while (this.combatIdleTimer >= msPerFrame) {
        this.combatIdleTimer -= msPerFrame;
        this.combatIdleIndex++;
        changed = true;
      }
      if (changed) this.applyFrame();
      return;
    }
    if (this.fidgetIndex < 0) {
      // Standing on the neutral frame; count down to the next fidget.
      this.idleTimer += dtMs;
      if (this.idleTimer >= this.nextFidgetAt) {
        this.fidgetIndex = START_FRAME;
        this.fidgetTimer = 0;
        this.applyFrame();
      }
      return;
    }

    // Mid-fidget: play the clip through once, then return to standing. The body
    // layer's fidget length drives the one-shot; shorter equipment clips clamp.
    const msPerFrame = 1000 / FIDGET_FPS;
    const count = this.bodyClips().fidget[this.facing]?.length ?? 1;
    this.fidgetTimer += dtMs;
    let changed = false;
    while (this.fidgetTimer >= msPerFrame) {
      this.fidgetTimer -= msPerFrame;
      this.fidgetIndex++;
      changed = true;
      if (this.fidgetIndex >= count) {
        // One-shot done: back to standing, schedule the next fidget.
        this.fidgetIndex = -1;
        this.idleTimer = 0;
        this.nextFidgetAt = randomFidgetDelay();
        break;
      }
    }
    if (changed) this.applyFrame();
  }

  /**
   * The frame **one layer** draws right now, given the shared mode/facing/index.
   * Each layer looks the index up in *its own* clips and clamps it into *its own*
   * range, so a layer with fewer frames for an action (a short hair clip, a bad
   * bake) can never throw or desync the others - it just holds its last in-range
   * frame. This is the seam that keeps the whole stack in lockstep.
   */
  private currentFrame(clips: PlayerFrames): FrameTexture | undefined {
    if (this.actionClip !== undefined) {
      // Mid one-shot action clip (ADR-0116): each layer plays the named clip,
      // clamped into its own range like every other action. A layer with no such
      // clip (an equipment layer whose swing was never baked) holds its stand
      // pose rather than vanishing or desyncing the stack.
      const stand = clips.stand[this.facing]?.[START_FRAME];
      const action = clipByName(clips, this.actionClip)?.[this.facing];
      if (!action || action.length === 0) return stand;
      return action[Math.min(this.actionIndex, action.length - 1)] ?? stand;
    }
    if (this.gaiting) {
      // In combat stance this is the combat advance (action 15); else the run/walk
      // cycle. Per-layer, so a layer missing the combat clip degrades to its walk.
      const gait = this.gaitFrames(clips);
      if (!gait || gait.length === 0) return undefined;
      // Clamp into range: run/walk can differ in frame count (a shared index may
      // overshoot after a gait swap), and a layer may be shorter than the body.
      //
      // A layer with a DIFFERENT cycle length from the clock owner is phase-mapped
      // rather than wrapped (ADR-0442). Equipment shares the body's action and so
      // always matches, making this a no-op there - but a mounted rider does not: the
      // clock counts the mount's stride (the mount is layer 0), and UO's 5-frame seated
      // cycle wrapped against a 10-frame camel stride would run the rider through two
      // bounces per step, visibly jittering them in the saddle. Stretching the shorter
      // cycle across the longer one keeps rider and mount in step.
      const clock = this.gaitFrames(this.bodyClips());
      const clockCount = clock?.length ?? gait.length;
      const index =
        clockCount > 0 && gait.length !== clockCount
          ? Math.floor(((this.walkIndex % clockCount) * gait.length) / clockCount)
          : this.walkIndex % gait.length;
      return gait[index] ?? gait[START_FRAME];
    }
    const stand = clips.stand[this.facing]?.[START_FRAME];
    // Standing in combat stance: loop the combat idle (action 7), degrading to the
    // stand pose for any layer that has no baked combat idle (ADR-0131).
    if (this.combatStance) {
      const idle = this.combatIdleFrames(clips);
      if (idle) return idle[this.combatIdleIndex % idle.length] ?? stand;
      return stand;
    }
    if (this.fidgetIndex < 0) {
      // Standing still: the dedicated single-frame static pose.
      return stand;
    }
    // Mid-fidget: play the shift/stretch clip, clamped to this layer's own length.
    const fidget = clips.fidget[this.facing];
    if (!fidget || fidget.length === 0) return stand;
    return fidget[Math.min(this.fidgetIndex, fidget.length - 1)] ?? stand;
  }

  /** Redraw every layer from the shared clock, each by its own frame + anchor. */
  private applyFrame(): void {
    let bodyFrame: FrameTexture | undefined;
    const resting = this.actionClip === undefined && !this.gaiting && !this.combatStance;
    // The body layer's own lift, so the overhead labels ride up with a mounted rider
    // rather than floating at the height the rider would have had on foot.
    let bodyOffsetY = 0;
    for (const layer of this.layers) {
      const frame = this.currentFrame(layer.clips);
      if (!frame) {
        // No frame for this layer/facing (e.g. unbaked action): draw nothing
        // rather than holding a stale texture from another facing.
        layer.sprite.texture = Texture.EMPTY;
        continue;
      }
      if (layer.layer === 'body') bodyFrame = frame;
      const lift =
        (layer.offsetY[this.facing] ?? 0) +
        (resting ? (layer.idleOffsetY[this.facing] ?? 0) : 0);
      layer.sprite.texture = frame.texture;
      const scale = layer.visualScale;
      layer.sprite.scale.set(scale);
      // Place the frame by its own UO anchor: the character's ground point sits at
      // the container origin, so top-left = (-cx, -cy - height). Each layer's frames
      // carry their own anchors, so they self-register against the body - including
      // the held light's hand-bounce, now that we play the armed gait (ADR-0277).
      //
      // Oversized equipment grows around the frame's visual centre rather than
      // Pixi's top-left origin or the character's feet. This keeps a 2x helmet
      // centred on the head while leaving the body at its normal scale.
      if (layer.scaleOrigin === 'ground') {
        layer.sprite.position.set(
          -frame.cx * scale,
          GROUND_OFFSET_Y - (frame.cy + frame.texture.height) * scale - lift,
        );
      } else {
        const extraX = ((scale - 1) * frame.texture.width) / 2;
        const extraY = ((scale - 1) * frame.texture.height) / 2;
        layer.sprite.position.set(
          -frame.cx - extraX,
          -frame.cy - frame.texture.height - extraY + GROUND_OFFSET_Y - lift,
        );
      }
      if (layer.layer === 'body') bodyOffsetY = lift;
    }
    this.setOverheadAnchor(bodyFrame, bodyOffsetY);
  }

  /**
   * Match ClassicUO's mobile overhead positioning against the current body
   * animation dimensions instead of a fixed human-height guess.
   */
  private setOverheadAnchor(frame: FrameTexture | undefined, bodyOffsetY = 0): void {
    const next = frame
      ? GROUND_OFFSET_Y - frame.cy - frame.texture.height - 8 - bodyOffsetY
      : NAME_OFFSET_Y;
    if (next === this.overheadAnchorY) return;
    this.overheadAnchorY = next;
    if (this.nameLabel) this.nameLabel.position.set(0, next);
    if (this.titleLabel) this.titleLabel.position.set(0, next - TITLE_LINE_GAP);
    if (this.banishLabel) this.banishLabel.position.set(0, next - 16);
    this.layoutSpeech();
    this.layoutCombatText();
  }
}
